/**
 * @ngdoc Services
 * @name app.services.common
 * @description
 * The common service is used for getting and setting common vars used across the application
 *
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';

import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { environment } from '@environment/environment';
import { COMMON_DATE_FORMAT, DM_PREFIX } from '@configs/app-settings.config';
import { appMessageBase, errorMessage } from '@app/core/constants/app-message.constant';
import { JWTAuthService } from './jwt-auth.service';
import { AppStatusEnum, SnackClassEnum } from '../helpers/enums/enums';
import { ApiResponseModel } from '@app/modules/shared/models/api-response-model';
import { LoggerService } from './logger.service';
import { PubSubService } from './pub-sub-service/pub-sub.service';
import { addMinute, sleep } from '../helpers/utils';
import { UserService } from './user.service';


@Injectable()
export class CommonService {
  refreshIntervalId: any

  constructor(
    public snackBar: MatSnackBar,
    public router: Router,
    private date: DatePipe,
    private readonly jwtService: JWTAuthService,
    private readonly logger: LoggerService,
    private readonly userService: UserService,
    private readonly pubSubService: PubSubService
  ) { }

  /*** Get api base url
    * @name getApiBaseUrl
    * @param  none
    * @return {String}
    */
  getApiBaseUrl() {
    return environment.host;
  }


  lowerCaseUnderScore(str = '') {
    if (typeof (str === 'string')) {
      return str.replace(/\s+/g, '_').toLowerCase();
    }
  }

  transErrorResponse(response) {
    response = response ? response.json() : {};
    return response;
  }

  /*** Show snake bar message
    * @name showSnakeBar
    * @param  msg {String, action:action to show}
    * @return none
    */
  showSnakeBar(msg: string, options:
    {
      action?: string, duration?: number,
      horizontalPosition?: MatSnackBarHorizontalPosition,
      verticalPosition?: MatSnackBarVerticalPosition,
      pclass?: SnackClassEnum
    } = {}) {
    this.snackBar.open(msg, (options.action || 'Dismiss'), {
      duration: (options.duration || 5e3),
      horizontalPosition: (options.horizontalPosition || 'center'),
      verticalPosition: (options.verticalPosition || 'top'),
      panelClass: options.pclass ? [options.pclass] : null
    });
  }

  /**
   * show error alert
   * @param msg 
   */
  showErrorAlert(msg: string, duration = 5e3) {
    this.showSnakeBar(msg, {
      pclass: SnackClassEnum.Error,
      duration
    })
  }

  /**
   * sleep 
   * @param time 
   */
  async sleep(time?: number) {
    return new Promise((res: any, rej: any) => {
      setTimeout(() => {
        res(true)
      }, (time || 5000));
    })
  }

  /*** Showing common error message
    * @name commonSnakeBar
    * @param  none
    * @return none
    */
  commonSnakeBar() {
    this.showSnakeBar(errorMessage.httpError);
  }

  /*** navigate user to given url
  * @name navigate
  * @param  url {String}
  * @return none
  */
  navigate(url) {
    this.router.navigate([url]);
  }

  navigateSkipLocation(url) {
    this.router.navigateByUrl(url, { skipLocationChange: true });
  }


  /*** Transform date
  * @name transFormDate
  * @param  date, format(Date Format)
  * @return String
  */
  transFormDate(date, format = null) {
    let dateFormat = COMMON_DATE_FORMAT;
    if (format != null) {
      dateFormat = format;
    }
    return this.date.transform(date, dateFormat);
  }

  /**
   * add items to local storage
   * @param key 
   * @param value 
   */
  setLocalStorage(key: string, value: any, isStringifyJson = false) {
    try {
      if (typeof value === "object" && isStringifyJson) {
        value = this.stringfyJson(value)
      }
    } catch (error) {
      this.logger.log('error in conversion of value to jsonstring ');
    }
    window.localStorage.setItem(DM_PREFIX + key, value);
  }
  /**
   * remove items to local storage
   * @param key 
   */
  removeLocalStorage(key: string) {
    window.localStorage.removeItem(DM_PREFIX + key);
  }
  /**
   * get items to local storage
   * @param key 
   */
  getLocalStorage(key: string, isParseJason = false) {
    let value = window.localStorage.getItem(DM_PREFIX + key);
    try {
      if (isParseJason) {
        value = this.parseJson(value);
      }
    } catch (error) {
      this.logger.error('value is not json string')
    }
    return (value as any);
  }
  /**
   * get user info
   */
  async getuserInfo() {
    try {
      let user = await this.jwtService.getUserInfo().toPromise();
      return user;
    } catch (error) {
      throw error;
    }
  }
  /**
   * logout 
   */
  logout() {
    this.jwtService.logout();
  }
  /**
   * stringfy obj to json string
   * @param obj 
   */
  stringfyJson(obj: any) {
    return JSON.stringify(obj);
  }
  /**
   * parse json string
   * @param strJson 
   */
  parseJson(strJson: string) {
    return JSON.parse(strJson);
  }
  /**
   * get user subscription
   */
  getUserSubscription() {
    return this.jwtService.getUserSubscription();
  }

  /**
   * it check whether space is available in storage that is used for storing local recording in index db
   * @returns 
   */
  async isSpaceAvailableLocalRecording() {
    let res = false;
    try {
      let storageEstimate = await this.getStorageEstimateInfo();
      if (storageEstimate) {
        res = true;
        const { availableSpace, description } = storageEstimate
        if (availableSpace < 2000) {
          res = false;
          this.logger.log("not Enough Space Available <= 2GB", {
            storageEstimate,
            availableSpace
          });
        }
        else {
          this.logger.log(`${description}: availableSpace = ${availableSpace}`)
        }
      }
      return res;
    } catch (error) {
      throw error;
    }
  }

  /**
   * get storage estimation with extended information
   * @returns 
   */
  async getStorageEstimateInfo() {
    try {
      let storageEstimate = await this.getStorageEstimate();
      if (storageEstimate) {
        const { usage, quota } = storageEstimate
        var percentUsed = Math.round(usage / quota * 100),
          usageInMib = Math.round(usage / 1048576)
          , quotaInMib = Math.round(quota / 1048576)
          , description = `${usageInMib} out of ${quotaInMib} MiB used ${percentUsed} %`;
        var availableSpace = quotaInMib - usageInMib;
        return {
          percentUsed,
          usageInMib,
          quotaInMib,
          availableSpace,
          description
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * get estimation of storage 
   * @returns 
   */
  async getStorageEstimate() {
    let storageEstimate: StorageEstimate = null;
    try {
      if ("storage" in navigator && "estimate" in navigator.storage) {
        storageEstimate = await navigator.storage.estimate();
      }
      else {
        this.logger.log('getStorageEstimate_webkitTemporaryStorage');
        const webkitTemporaryStorage = (navigator as any).webkitTemporaryStorage
        if ("webkitTemporaryStorage" in navigator && "queryUsageAndQuota" in webkitTemporaryStorage) {
          storageEstimate = await new Promise((function (res, rej) {
            webkitTemporaryStorage.queryUsageAndQuota((function (t: number, r: number) {
              res({
                usage: t,
                quota: r
              })
            }), rej)
          }));
        }
      }
      return storageEstimate;
    } catch (error) {
      throw error;
    }
  }


  /**
   * This method handle the refresh token
   * token will be refresh before 5mintue of expire
   */
  async refreshTokenHandler() {
    try {
      if (this.refreshIntervalId) return;
      let user = await this.getuserInfo();
      if (user) {
        console.log('user tok', user);
        const tokenRefreshMin = -5;
        const exp = user.exp,
          expTime = addMinute(new Date(exp * 1e3), tokenRefreshMin),
          date = new Date(),
          duration = expTime.getTime() - date.getTime();
        this.refreshIntervalId = setTimeout(() => {
          console.log('refresh token requested', duration, this.refreshIntervalId)
          this.refreshAndSaveToken();
        }, duration);
      }
    } catch (error) {
      console.error('refreshTokenHandler', error)
    }
  }

  /**
   * this method get new token and update with old one
   */
  private refreshAndSaveToken() {
    this.userService.refreshToken().subscribe(async (res: ApiResponseModel) => {
      if (res.data) {
        this.jwtService.setLoginUserDetail(res.data);
        this.refreshIntervalId = 0;
        await sleep()
        this.refreshTokenHandler();
      }
    }, (err: any) => {
      this.logger.error('refreshAndSaveToken', err)
    })
  }


  startLoader() {
    this.pubSubService.startStopLoader(true);
  }


  stopLoader() {
    this.pubSubService.startStopLoader();
  }

  detectDomainAlreadyOpened() {
    return new Promise(async (res, rej) => {
      console.log('detectDomainAlreadyOpened')
      localStorage.openpages = Date.now();
      var onLocalStorageEvent = function (e) {
        if (e.key == "openpages") {
          // Emit that you're already available.
          localStorage.page_available = Date.now();
        }
        if (e.key == "page_available") {
          window.removeEventListener('storage', onLocalStorageEvent, false);
          console.log('page_available');
          res(true);
        }
      };
      window.addEventListener('storage', onLocalStorageEvent, false);
      await this.sleep(1e3);
      console.log('sleep for 3 sec');
      res(false)
    })
  }

}
